import axios from 'axios';
import * as Sentry from '@sentry/react';

// Use relative URL for API requests - Cloudflare Functions proxy handles backend routing
// This keeps the API key server-side and never exposes it to the browser
const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Response interceptor for error handling with Sentry reporting
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    // Report 5xx errors and network failures to Sentry
    if (import.meta.env.VITE_SENTRY_DSN && (!status || status >= 500)) {
      Sentry.captureException(error, {
        tags: { source: 'api', url: error.config?.url },
        extra: { status, method: error.config?.method }
      });
    }
    return Promise.reject(error);
  }
);

export const productsAPI = {
  // Get all products with filters
  // Supports: category, brand, minPrice, maxPrice, sort, limit, offset, lang, tags, vendor, page
  getProducts: async (params = {}) => {
    const response = await api.get('/v1/public/products', { params });
    return response.data;
  },

  // Get single product by ID
  // Supports optional lang parameter for multi-language support
  getProductById: async (id, params = {}) => {
    const response = await api.get(`/v1/public/products/${id}`, { params });
    return response.data;
  },

  // Get minimal product data for quick view modal
  // Returns basic info without full variants/offers details
  getProductQuickView: async (id, params = {}) => {
    const response = await api.get(`/v1/public/products/${id}/quick-view`, { params });
    return response.data;
  },

  // Get related products for a specific product
  // Uses 3 strategies: same category + similar price, same brand, shared tags
  // Supports: limit (default: 12, max: 24)
  getRelatedProducts: async (id, params = {}) => {
    const response = await api.get(`/v1/public/products/${id}/related`, { params });
    return response.data;
  },

  // Get products by category slug
  // Supports: page, limit, sort (newest, price_asc, price_desc), lang
  getProductsByCategory: async (categorySlug, params = {}) => {
    const response = await api.get(`/v1/public/products/by-category/${categorySlug}`, { params });
    return response.data;
  },

  // Product filter with facets (brands, categories, price ranges)
  // Supports: page, limit, category, brand, minPrice, maxPrice, includeFilters, lang
  getProductsFilter: async (params = {}) => {
    const response = await api.get('/v1/public/products/filter', { params });
    return response.data;
  },

  // Dedicated product search with ranking
  // Supports: search term + all filter params (category, brand, price, sort, lang, tags)
  searchProducts: async (searchTerm, params = {}) => {
    const response = await api.get('/v1/public/products/search', {
      params: { q: searchTerm, ...params }
    });
    return response.data;
  },

  // Autocomplete suggestions for search input
  // Supports: q (min 2 chars), limit (default 10, max 20)
  autocomplete: async (query, params = {}) => {
    const response = await api.get('/v1/public/products/autocomplete', {
      params: { q: query, ...params }
    });
    return response.data;
  },

  // Compare multiple products side-by-side
  // Accepts array of product IDs and returns comparison data
  compareProducts: async (productIds, params = {}) => {
    const response = await api.post('/v1/public/products/compare', {
      productIds
    }, { params });
    return response.data;
  },

  // Get price history (daily min/avg points + lowest/current summary) for a product
  // Supports: range (7d|30d|90d, default 30d)
  // Response is wrapped in the BE envelope {success, data:{...}, timestamp} —
  // caller reads data.data.points / data.data.summary (no interceptor unwraps it).
  getPriceHistory: async (id, params = {}) => {
    const response = await api.get(`/v1/public/products/${id}/price-history`, { params });
    return response.data;
  }
};

export const categoriesAPI = {
  // Get all categories
  // Supports optional lang parameter for multi-language support
  getCategories: async (params = {}) => {
    const response = await api.get('/v1/public/categories', { params });
    return response.data;
  },

  // Get single category by ID or slug with full SEO content (intro, FAQ, tips)
  // Supports optional lang parameter for multi-language support
  getCategoryById: async (id, params = {}) => {
    const response = await api.get(`/v1/public/categories/${id}`, { params });
    return response.data;
  },

  // Get hierarchical category tree with product counts and parent-child relationships
  // Supports optional lang parameter for multi-language support
  getCategoryTree: async (params = {}) => {
    const response = await api.get('/v1/public/categories/tree', { params });
    return response.data;
  }
};

export const brandsAPI = {
  // Get all brands
  // Supports optional lang parameter for multi-language support
  getBrands: async (params = {}) => {
    const response = await api.get('/v1/public/brands', { params });
    return response.data;
  }
};

export const vendorsAPI = {
  // Get all vendors (slug + product count)
  // Supports optional lang parameter for multi-language support
  getVendors: async (params = {}) => {
    const response = await api.get('/v1/public/vendors', { params });
    return response.data;
  },

  // Get vendor details + product stats by slug (falls back to vendor UUID)
  // Returns: id, name, slug, logo, createdAt, stats { productCount, offerCount, brandCount, priceRange }
  // Supports optional lang parameter for multi-language support
  getVendorBySlug: async (slug, params = {}) => {
    const response = await api.get(`/v1/public/vendors/${slug}`, { params });
    return response.data;
  }
};

export const guidesAPI = {
  // List published buying guides (summary fields)
  getGuides: async (params = {}) => {
    const response = await api.get('/v1/public/guides', { params });
    return response.data;
  },
  // Get a single published guide by slug (full content)
  getGuideBySlug: async (slug, params = {}) => {
    const response = await api.get(`/v1/public/guides/${slug}`, { params });
    return response.data;
  }
};

export const tagsAPI = {
  // Get all product tags with optional filtering
  // Supports: type (technical, feature, material, use_case), search, limit, offset, lang
  getTags: async (params = {}) => {
    const response = await api.get('/v1/public/tags', { params });
    return response.data;
  },

  // Get tag details and products with that tag
  // Supports: limit, offset, lang
  getTagBySlug: async (slug, params = {}) => {
    const response = await api.get(`/v1/public/tags/${slug}`, { params });
    return response.data;
  }
};

export default api;
